import clsx from "clsx";
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Link } from "react-router-dom";
import Icon, { type IconName } from "../components/Icon";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "danger"
  | "success"
  | "subtle";
export type ButtonSize = "sm" | "md" | "lg";

const variants: Record<ButtonVariant, string> = {
  primary: "bg-accent text-accent-fg shadow-xs hover:bg-accent-hover active:bg-accent-hover",
  secondary:
    "border-line bg-surface text-ink shadow-xs hover:bg-sunken hover:border-line-strong",
  ghost: "text-muted hover:bg-sunken hover:text-ink",
  subtle: "bg-sunken text-ink hover:bg-neutral-line",
  danger: "bg-danger text-white shadow-xs hover:brightness-95",
  success: "bg-success text-success-fg shadow-xs hover:brightness-95",
};

const sizes: Record<ButtonSize, string> = {
  sm: "h-7 px-2.5 text-xs gap-1 rounded",
  md: "h-8 px-3 text-sm gap-1.5 rounded-md",
  lg: "h-10 px-4 text-md gap-2 rounded-md",
};

const iconSizes: Record<ButtonSize, number> = { sm: 14, md: 15, lg: 17 };

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  /** Иконка справа — для «дальше», «открыть», раскрывающихся кнопок. */
  iconEnd?: IconName;
  loading?: boolean;
  block?: boolean;
  children?: ReactNode;
  className?: string;
}

function classes({ variant = "secondary", size = "md", block, className }: CommonProps) {
  return clsx(
    "inline-flex items-center justify-center whitespace-nowrap border border-transparent",
    "font-medium transition-colors duration-100 select-none",
    "disabled:opacity-45 disabled:pointer-events-none",
    variants[variant],
    sizes[size],
    block && "w-full",
    className,
  );
}

function content({ icon, iconEnd, loading, children, size = "md" }: CommonProps) {
  const iconSize = iconSizes[size];
  return (
    <>
      {loading ? (
        <Spinner size={iconSize} />
      ) : (
        icon && <Icon name={icon} size={iconSize} />
      )}
      {children}
      {iconEnd && !loading && <Icon name={iconEnd} size={iconSize} />}
    </>
  );
}

export type ButtonProps = CommonProps & ButtonHTMLAttributes<HTMLButtonElement>;

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, icon, iconEnd, loading, block, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={rest.type ?? "button"}
      {...rest}
      disabled={rest.disabled || loading}
      className={classes({ variant, size, block, className })}
    >
      {content({ icon, iconEnd, loading, children, size })}
    </button>
  );
});

export default Button;

/** Ссылка, визуально неотличимая от кнопки. */
export function ButtonLink({
  to,
  variant,
  size,
  icon,
  iconEnd,
  block,
  className,
  children,
  ...rest
}: CommonProps & { to: string } & Omit<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    "href"
  >) {
  return (
    <Link to={to} {...rest} className={classes({ variant, size, block, className })}>
      {content({ icon, iconEnd, children, size })}
    </Link>
  );
}

/** Квадратная кнопка с одной иконкой. `label` становится доступным именем. */
export const IconButton = forwardRef<
  HTMLButtonElement,
  { icon: IconName; label: string; variant?: ButtonVariant; size?: ButtonSize } & Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    "children"
  >
>(function IconButton({ icon, label, variant = "ghost", size = "md", className, ...rest }, ref) {
  const box = size === "sm" ? "h-7 w-7" : size === "lg" ? "h-10 w-10" : "h-8 w-8";
  return (
    <button
      ref={ref}
      type={rest.type ?? "button"}
      aria-label={label}
      title={label}
      {...rest}
      className={clsx(
        "inline-flex items-center justify-center rounded-md border border-transparent",
        "transition-colors duration-100 disabled:opacity-45 disabled:pointer-events-none",
        variants[variant],
        box,
        className,
      )}
    >
      <Icon name={icon} size={iconSizes[size]} />
    </button>
  );
});

export function Spinner({ size = 16, className = "" }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={clsx("animate-spin", className)}
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.2" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
